// packages/client/src/components/project/miller/RevisionColumn.tsx
import { Button } from '@heroui/react';
import { Eye, Clock } from 'lucide-react';
import StatusBadge from './StatusBadge';
import type { DrawingRevision } from '@estimator/shared';

interface RevisionColumnProps {
  revision: DrawingRevision;
  onOpenViewer: (knowledgeItemId: string, revisionId: string) => void;
}

export default function RevisionColumn({ revision, onOpenViewer }: RevisionColumnProps) {
  return (
    <div
      className="flex flex-col h-full"
      style={{ minWidth: 200 }}
    >
      <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Revision {revision.revision_number}
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Status */}
        <div className="space-y-1">
          <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
            Status
          </span>
          <div><StatusBadge status={revision.status} /></div>
        </div>

        {/* Submitted info */}
        {revision.submitted_by_name && (
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Submitted by
            </span>
            <p className="text-xs" style={{ color: 'var(--text-primary)' }}>
              {revision.submitted_by_name}
            </p>
            {revision.submitted_at && (
              <p className="text-[10px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                <Clock size={10} />
                {new Date(revision.submitted_at).toLocaleDateString(undefined, {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                })}
              </p>
            )}
          </div>
        )}

        {/* Reviewer info */}
        {revision.reviewed_by_name && (
          <div className="space-y-1">
            <span className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              Reviewed by
            </span>
            <p className="text-xs" style={{ color: 'var(--text-primary)' }}>
              {revision.reviewed_by_name}
            </p>
          </div>
        )}

        {/* Review notes */}
        {revision.review_notes && (
          <div
            className="text-xs p-2 rounded border"
            style={{
              borderColor: 'var(--yellow)',
              backgroundColor: 'color-mix(in srgb, var(--yellow) 5%, transparent)',
            }}
          >
            <p className="font-medium mb-1" style={{ color: 'var(--yellow)' }}>Review Notes:</p>
            <p style={{ color: 'var(--text-secondary)' }}>{revision.review_notes}</p>
          </div>
        )}

        {/* Open button */}
        <Button
          fullWidth
          color="primary"
          size="sm"
          startContent={<Eye size={14} />}
          onPress={() => onOpenViewer(revision.knowledge_item_id, revision.id)}
        >
          Open in Viewer
        </Button>
      </div>
    </div>
  );
}
